import React, { useState, useEffect} from 'react';
import Papa from 'papaparse'; // Import Papaparse for parsing CSV files
import Footer from "./Footer";
import "./UmamiDBPredictorPage.css";

const UmamiDBPredictorPage = () => {
  const [activeTab, setActiveTab] = useState('Single');
  const [smiles, setSmiles] = useState('');
  const [miniBatchInput, setMiniBatchInput] = useState('');
  const [batchFile, setBatchFile] = useState(null);
  const [predictions, setPredictions] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSmilesChange = (newSmiles) => {
    setSmiles(newSmiles);
  };
  // Function to change the active tab
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPredictions([]);
    setError('');
    setSmiles('');
  };

  // Function to handle input change for the single SMILES input
  const handleInputChange = (event) => {
    setSmiles(event.target.value);
  };

  // Function to handle input change for the MiniBatch SMILES input
  const handleMiniBatchInputChange = (event) => {
    setMiniBatchInput(event.target.value);
  };

  // Function to handle file selection for the Batch input
  const handleFileChange = (event) => {
    setBatchFile(event.target.files[0]);
  };

  // Function to handle single prediction
  const handlePrediction = async () => {
    if (!smiles) {
      setError('Please enter a SMILES sequence.');
      return;
    }

    setLoading(true);
    setError('');
    setPredictions([]);

    try {
      const response = await fetch('/umami-predict-api/predict', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ smiles: smiles }),
      });

      const data = await response.json();

      if (response.ok) {
        setPredictions([`Prediction: ${data.prediction}`]);
      } else {
        setError(`Error: ${data.error}`);
      }
    } catch (err) {
      setError('Error: Unable to connect to the prediction API');
    } finally {
      setLoading(false);
    }
  };

  // Function to handle MiniBatch predictions
  const handleMiniBatchPrediction = async () => {
    const smilesList = miniBatchInput.split('\n').map((line) => line.trim()).filter((line) => line);
    if (smilesList.length === 0) {
      setError('Please enter at least one SMILES sequence.');
      return;
    }

    setLoading(true);
    setError('');
    setPredictions([]);

    try {
      const allPredictions = [];
      for (const smile of smilesList) {
        const response = await fetch('/umami-predict-api/predict', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ smiles: smile }),
        });

        const data = await response.json();
        if (response.ok) {
          allPredictions.push(`${smile}: ${data.prediction}`);
        } else {
          allPredictions.push(`${smile}: Error - ${data.error}`);
        }
      }
      setPredictions(allPredictions);
    } catch (err) {
      setError('Error: Unable to connect to the prediction API');
    } finally {
      setLoading(false);
    }
  };

  // Function to handle Batch predictions using a CSV file
  const handleBatchPrediction = () => {
    if (!batchFile) {
      setError('Please upload a CSV file.');
      return;
    }

    setLoading(true);
    setError('');
    setPredictions([]);

    Papa.parse(batchFile, {
      complete: async (result) => {
        const smilesList = result.data.map((row) => row[0]).filter((smile) => smile);
        if (smilesList.length === 0) {
          setError('The uploaded file does not contain any SMILES strings.');
          setLoading(false);
          return;
        }

        try {
          const allPredictions = [];
          for (const smile of smilesList) {
            const response = await fetch('/umami-predict-api/predict', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ smiles: smile }),
            });

            const data = await response.json();
            if (response.ok) {
              allPredictions.push(`${smile}: ${data.prediction}`);
            } else {
              allPredictions.push(`${smile}: Error - ${data.error}`);
            }
          }
          setPredictions(allPredictions);
        } catch (err) {
          setError('Error: Unable to connect to the prediction API');
        } finally {
          setLoading(false);
        }
      },
      header: false,
    });
  };

  useEffect(() => {
    if (activeTab !== 'Drawing') {
      setSmiles(''); // Reset SMILES on tab change (optional)
      return;
    }

    let cancelled = false;
    const containerId = 'jsme-editor';

    const initJsme = () => {
      if (cancelled) return;
      // Debug logs to understand why JSME might not appear
      // These will show up in the browser console.
      // eslint-disable-next-line no-console
      console.log('initJsme tick', {
        hasWindow: typeof window !== 'undefined',
        hasJSApplet: !!(window && window.JSApplet),
        hasJSMEApplet: !!(window && window.JSApplet && window.JSApplet.JSME),
        hasGlobalJSME: !!(window && window.JSME),
      });
      if (window.JSApplet && window.JSApplet.JSME) {
        const container = document.getElementById(containerId);
        if (container && !container._jsmeInstance) {
          // eslint-disable-next-line no-console
          console.log('Creating JSME instance in container', containerId);
          const jsme = new window.JSApplet.JSME(
            containerId,
            `${Math.min(400, window.innerHeight * 0.5)}px`,
            '100%'
          );
          container._jsmeInstance = jsme;
          jsme.setAfterStructureModifiedCallback(() => {
            const smilesString = jsme.smiles();
            handleSmilesChange(smilesString);
          });
        }
      } else if (window.JSME) {
        const container = document.getElementById(containerId);
        if (container && !container._jsmeInstance) {
          // eslint-disable-next-line no-console
          console.log('Creating global JSME instance in container', containerId);
          const jsme = new window.JSME(
            containerId,
            `${Math.min(400, window.innerHeight * 0.5)}px`,
            '100%'
          );
          container._jsmeInstance = jsme;
          if (typeof jsme.setAfterStructureModifiedCallback === 'function') {
            jsme.setAfterStructureModifiedCallback(() => {
              const smilesString = jsme.smiles();
              handleSmilesChange(smilesString);
            });
          }
        }
      } else {
        // Retry shortly until JSME script is loaded
        setTimeout(initJsme, 200);
      }
    };

    initJsme();

    return () => {
      cancelled = true;
    };
  }, [activeTab]);
  
    // Rendering logic for the "Drawing" tab
    const renderDrawingTab = () => (
      <div className="drawing-content d-flex flex-column align-items-center">
        <h3>Drawing</h3>
        <div
          style={{
            margin: '20px 0',
            width: '100%',
            maxWidth: '600px',
          }}
        >
          <div id="jsme-editor" style={{ width: '100%', height: `${Math.min(400, window.innerHeight * 0.5)}px` }} />
        </div>
        <div
          className="alert alert-info mt-3 text-center"
          style={{
            maxWidth: '600px',
            width: '100%', // Make the alert width responsive
          }}
        >
          <strong>SMILES:</strong> {smiles || 'Draw a molecule to see the SMILES here.'}
        </div>
        {/* Add the Submit button for prediction */}
        <button
          className="btn btn-submit mt-3 btn-custom"
          onClick={handlePrediction}
          disabled={loading || !smiles}
          style={{ maxWidth: '200px', width: '100%' }} // Make the button responsive
        >
          {loading ? 'Loading...' : 'Submit'}
        </button>
        {/* Display predictions below the Submit button */}
        {predictions.length > 0 && (
          <div
            className="alert alert-info text-center mt-3"
            style={{
              maxWidth: '600px',
              width: '100%', // Make the alert width responsive
            }}
          >
            {predictions[0]}
          </div>
        )}
        {/* Display errors if any */}
        {error && (
          <div
            className="alert alert-danger text-center mt-3"
            style={{
              maxWidth: '600px',
              width: '100%', // Make the alert width responsive
            }}
          >
            {error}
          </div>
        )}
      </div>
    );
    


return (
  <>
    <section className="umami-predictor m-3" style={{ paddingTop: '100px', paddingBottom: '100px' }}>
      <div className="container">
        <h2 className="text-center mb-3">UmamiDB Predictor 🔮</h2>
        <p className="text-center">
          Enter a SMILES string, draw a molecule, or upload a CSV to predict the Umami.
        </p>
        <div className="tabs-container">
          <div className="tabs d-flex flex-wrap justify-content-center">
            <button
              className={`tab-button ${activeTab === 'Single' ? 'active' : ''}`}
              onClick={() => handleTabChange('Single')}
            >
              Single
            </button>
            <button
              className={`tab-button ${activeTab === 'Batch' ? 'active' : ''}`}
              onClick={() => handleTabChange('Batch')}
            >
              Batch
            </button>
            <button
              className={`tab-button ${activeTab === 'Drawing' ? 'active' : ''}`}
              onClick={() => handleTabChange('Drawing')}
            >
              Drawing
            </button>
            <button
              className={`tab-button ${activeTab === 'MiniBatch' ? 'active' : ''}`}
              onClick={() => handleTabChange('MiniBatch')}
            >
              MiniBatch
            </button>
          </div>
          <div className="tab-content">
            {activeTab === 'Single' && (
              <div className="single-content">
                <h3 className="text-center">Single</h3>
                <div className="form-group">
                  <input
                    type="text"
                    placeholder="Enter SMILES sequence"
                    className="form-control"
                    value={smiles}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="text-center">
                  <button
                    className="btn btn-submit mt-3 btn-custom"
                    onClick={handlePrediction}
                    disabled={loading}
                  >
                    {loading ? 'Loading...' : 'Submit'}
                  </button>
                </div>
                {predictions.length > 0 && (
                  <div className="alert alert-info text-center mt-3" role="alert">
                    {predictions[0]}
                  </div>
                )}
                {error && (
                  <div className="alert alert-danger text-center mt-3" role="alert">
                    {error}
                  </div>
                )}
              </div>
            )}
            {activeTab === 'Batch' && (
              <div className="batch-content">
                <h3 className="text-center">Batch</h3>
                <div className="form-group">
                  <input type="file" className="form-control" onChange={handleFileChange} />
                </div>
                <div className="text-center">
                  <button
                    className="btn btn-submit mt-3 btn-custom"
                    onClick={handleBatchPrediction}
                    disabled={loading}
                  >
                    {loading ? 'Loading...' : 'Submit'}
                  </button>
                </div>
                {predictions.length > 0 && (
                  <div className="alert alert-info mt-3" role="alert">
                    <ul>
                      {predictions.map((pred, index) => (
                        <li key={index}>{pred}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {error && (
                  <div className="alert alert-danger mt-3" role="alert">
                    {error}
                  </div>
                )}
              </div>
            )}
            {activeTab === 'MiniBatch' && (
              <div className="minibatch-content">
                <h3 className="text-center">MiniBatch</h3>
                <div className="form-group">
                  <textarea
                    className="form-control"
                    placeholder="Enter SMILES strings, one per line"
                    value={miniBatchInput}
                    onChange={handleMiniBatchInputChange}
                  ></textarea>
                </div>
                <div className="text-center">
                  <button
                    className="btn btn-submit mt-3 btn-custom"
                    onClick={handleMiniBatchPrediction}
                    disabled={loading}
                  >
                    {loading ? 'Loading...' : 'Submit'}
                  </button>
                </div>
                {predictions.length > 0 && (
                  <div className="alert alert-info mt-3" role="alert">
                    <ul>
                      {predictions.map((pred, index) => (
                        <li key={index}>{pred}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {error && (
                  <div className="alert alert-danger mt-3" role="alert">
                    {error}
                  </div>
                )}
              </div>
            )}
            {activeTab === 'Drawing' && renderDrawingTab()}
          </div>
        </div>
      </div>
    </section>
    <Footer />
  </>
);

};

export default UmamiDBPredictorPage;
